import { useBoolean, useBreakpoints } from "@aurem/ui";
import { useLocalStorage } from "@aurem/utils";
import { ProductAssignmentSelector } from "components/ProductAssignmentSelector";
import { Topbar } from "components/Topbar";
import { PageTitleProvider } from "components/Topbar/PageTitle";
import { BannerStack, useBanner } from "providers/BannerProvider";
import { useEmployment } from "providers/employee/EmploymentProvider";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { EmployeeBottomNavigation } from "../../../components/mobile/BottomNavigation/EmployeeBottomNavigation";
import { LayoutInnerSidebar } from "../LayoutInnerSidebar";
import { LayoutOuterSidebar } from "../LayoutOuterSidebar";

const POST_TERMINATION_ALERT = "postTerminationAlertDismissed";
const POST_TERMINATION_BANNER_ID = "post-termination-banner";

const PostTerminationBanner = () => {
  const { t } = useI18n();
  const { isTerminated, employment } = useEmployment();
  const companyName = employment?.company.name || "";
  const { upsertBanner, removeBanner } = useBanner();
  const [isDismissed, setIsDismissed] = useLocalStorage(
    POST_TERMINATION_ALERT,
    false,
  );
  const { isMobile } = useBreakpoints();

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    removeBanner(POST_TERMINATION_BANNER_ID);
  }, [removeBanner, setIsDismissed]);

  useEffect(() => {
    if (!isTerminated || isDismissed) {
      removeBanner(POST_TERMINATION_BANNER_ID);
      return;
    }

    upsertBanner({
      id: POST_TERMINATION_BANNER_ID,
      props: {
        color: "neutral",
        title: t("apps.employee.pages.dashboard.page.banner", companyName),
        text: t("apps.employee.pages.dashboard.page.subscriptionStop"),
        layout: isMobile ? "stacked" : "inline",
        actions: {
          content: t("apps.admin.common.dismiss"),
          onClick: handleDismiss,
        },
      },
    });
  }, [
    handleDismiss,
    isDismissed,
    isTerminated,
    removeBanner,
    t,
    upsertBanner,
    isMobile,
  ]);

  return null;
};

export const EmployeeLayout = () => {
  const { isMobile } = useBreakpoints();
  const { employmentId } = useParams();

  const showBottomNavigation = isMobile && !!employmentId;

  const {
    value: showOuterSidebar,
    setTrue: openOuterSidebar,
    setFalse: closeOuterSidebar,
  } = useBoolean();

  return (
    <div className="flex flex-grow">
      <LayoutOuterSidebar open={showOuterSidebar} onClose={closeOuterSidebar} />
      {!isMobile && <LayoutInnerSidebar />}
      <PageTitleProvider>
        <div
          className="flex flex-col grow min-h-0 overflow-y-hidden w-full"
          style={{ maxHeight: "100dvh" }}
        >
          <Topbar openOuterSidebar={openOuterSidebar} />
          {isMobile && !!employmentId && (
            <div className="px-2 border-b border-b-neutral-100">
              <ProductAssignmentSelector
                className="h-14 justify-between shrink-0 px-2 py-1.5 outline-none"
                popoverProps={{
                  placement: "bottom-start",
                  matchTriggerWidth: true,
                  offset: { mainAxis: 8, crossAxis: 0 },
                }}
              />
            </div>
          )}
          <div className="flex flex-col grow min-h-0 overflow-y-auto w-full">
            <PostTerminationBanner />
            <BannerStack />
            <Outlet />
          </div>
          {showBottomNavigation && <EmployeeBottomNavigation />}
        </div>
      </PageTitleProvider>
    </div>
  );
};
